import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { SmallStyled } from 'approot/shared/styles/typography.style';
import { SETTINGS_MENU_ID } from 'approot/activity-builder/settings/settings-component-menu';
import {
  ContentFlexAlignment,
  FLEX_ALIGN_BOTTOM,
  FLEX_ALIGN_MIDDLE,
  FLEX_ALIGN_START,
  FLEX_SPACE_DISTRIBUTE,
  FLEX_SPACE_APART,
} from 'global/constants';
import { Dropdown, DropdownIndicator } from 'approot/shared/dropdown/dropdown';
import { ColumnDropdownButton } from './column-settings.style';
import {
  getAlignmentLabel,
  getColumnsColumnPayload,
} from './column-settings.utils';
import { SettingProps } from './column-settings.constants';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import { updateExistingColumnComponent } from 'approot/activity-builder/activity-builder.apollo.utils';
import IconSpaceBetween from 'approot/shared/inline-svg/activity-builder/icon-align-space-between';
import IconAlignTop from 'approot/shared/inline-svg/activity-builder/icon-align-top';
import IconAlignMiddle from 'approot/shared/inline-svg/activity-builder/icon-align-middle';
import IconAlignBottom from 'approot/shared/inline-svg/activity-builder/icon-align-bottom';
import IconAlignDistribute from 'approot/shared/inline-svg/activity-builder/icon-align-distribute';

const getAlignmentIconComponent = (alignment: ContentFlexAlignment | null) => {
  const props = { width: 20, height: 20 };
  switch (alignment) {
    case FLEX_SPACE_DISTRIBUTE:
      return <IconAlignDistribute {...props} />;
    case FLEX_SPACE_APART:
      return <IconSpaceBetween {...props} />;
    case FLEX_ALIGN_BOTTOM:
      return <IconAlignBottom {...props} />;
    case FLEX_ALIGN_MIDDLE:
      return <IconAlignMiddle {...props} />;
    default:
      return <IconAlignTop {...props} />;
  }
};

const ColumnSettingAligment = ({
  id,
  sectionId,
  type,
  sort,
  columnProperties,
  columns,
}: SettingProps) => {
  const client = useApolloClient();

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const alignment = columns?.[0].alignment || 'start';

  const handleUpdate = useCallback(
    (alignment: ContentFlexAlignment) => {
      const columnsPayload = getColumnsColumnPayload(columns).map(column => ({
        ...column,
        alignment,
      }));
      columnsPayload.forEach(col => {
        updateExistingColumnComponent({
          client,
          data: col,
          id: col.columnId,
          typename: 'ActivitiesColumnComponent',
        });
      });
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            type,
            sort,
            properties: {
              style: columnProperties?.style || null,
              columns: columnsPayload,
            },
          },
        },
        typename: 'ActivitiesColumnsComponent',
      });
    },
    [
      client,
      id,
      sectionId,
      sort,
      type,
      columns,
      columnProperties,
      updateComponentProperties,
    ]
  );

  return (
    <Dropdown
      isSmall
      menuWidth={250}
      menuContainerElement={SETTINGS_MENU_ID}
      strategy="fixed"
      closeMenuOnSelect={false}
      onMenuItemSelect={options => {
        setTimeout(() => options.closeDropdown());
      }}
      dropdownButtonContents={
        <ColumnDropdownButton data-selector="column-alignment">
          {getAlignmentIconComponent(alignment)}
          <DropdownIndicator />
        </ColumnDropdownButton>
      }
      dropdownContents={
        <SettingsGroup marginBottom={0}>
          <SettingsLabel>Alignment</SettingsLabel>
          <CheckableSingle
            variant="button"
            name="contentAligment"
            data-dropdown-item="true"
            options={[
              {
                label: <IconAlignTop width={24} height={24} />,
                'aria-label': 'Align column content to top',
                tooltipText: getAlignmentLabel(FLEX_ALIGN_START),
                value: FLEX_ALIGN_START,
              },
              {
                label: <IconAlignMiddle width={24} height={24} />,
                'aria-label': 'Align column content to middle',
                tooltipText: getAlignmentLabel(FLEX_ALIGN_MIDDLE),
                value: FLEX_ALIGN_MIDDLE,
              },
              {
                label: <IconAlignBottom width={24} height={24} />,
                'aria-label': 'Align column content to bottom',
                tooltipText: getAlignmentLabel(FLEX_ALIGN_BOTTOM),
                value: FLEX_ALIGN_BOTTOM,
              },
              {
                label: <IconSpaceBetween width={24} height={24} />,
                'aria-label': 'Space column content apart',
                tooltipText: getAlignmentLabel(FLEX_SPACE_APART),
                value: FLEX_SPACE_APART,
              },
              {
                label: <IconAlignDistribute width={24} height={24} />,
                'aria-label': 'Space column content evenly',
                tooltipText: getAlignmentLabel(FLEX_SPACE_DISTRIBUTE),
                value: FLEX_SPACE_DISTRIBUTE,
              },
            ]}
            selected={alignment}
            onSelect={value => handleUpdate(value)}
          />
          <SmallStyled marginBottom={0} marginTop={8}>
            {getAlignmentLabel(alignment)}
          </SmallStyled>
        </SettingsGroup>
      }
    />
  );
};

export default ColumnSettingAligment;
